import { visit } from 'unist-util-visit';

import CoreBase from '../../../../../services/engines/CoreBase';
import { isIndexBlockText, parseIndexBlock } from './indexBlockParser';
import { extractParagraphText } from '../mdastText';

/**
 * Detects "Name | RK | Description" index tables inside markdown paragraphs and rewrites
 * them into a custom hast node so MarkdownRenderer can render them as a proper table with
 * a clickable RK column. Companion to SourceTreeFormatter, same detect-in-text/render-as-
 * custom-component pattern.
 *
 * Runs after RkLinkifier in the remark pipeline, so the RK token in each line may already
 * be a `link` node by the time this visits — extractParagraphText (mdastText.js) walks the
 * full node tree (not just .value) so the hash survives.
 * Singleton — shared across all MarkdownRenderer instances.
 */
class IndexBlockFormatter extends CoreBase {
    _name = 'IndexBlockFormatter';

    /**
     * Returns a remark plugin that converts index-table paragraphs into 'indexblock' nodes.
     * @returns {Function} remark plugin
     */
    createRemarkPlugin = () => {
        return () => (tree) => {
            visit(tree, 'paragraph', this._visitParagraphNode);
        };
    };

    // ─── private ──────────────────────────────────────────

    /**
     * Visits a paragraph node — if its raw text looks like an index table,
     * replaces it with a custom hast element carrying the parsed rows.
     * @param {object} node - mdast paragraph node
     */
    _visitParagraphNode = (node) => {
        try {
            const rawText = extractParagraphText(node);
            if (!isIndexBlockText(rawText)) return;

            const rows = parseIndexBlock(rawText);
            node.children = [];
            node.data = { hName: 'indexblock', hProperties: { 'data-rows': JSON.stringify(rows) } };
        } catch (error) {
            this.logError('_visitParagraphNode', error.message);
        }
    };
}

const indexBlockFormatter = new IndexBlockFormatter();
export default indexBlockFormatter;
